import React, { useEffect } from "react";
import AOS from "aos";
import { Link } from "react-router-dom";
import "aos/dist/aos.css";
import { motion } from "framer-motion";
import { FaCalculator } from "react-icons/fa";

// Curriculum
const curriculum = [
  {
    title: "Introduction to Tally & Accounting Basics",
    icon: "📘",
    img: "https://img.icons8.com/color/96/ledger.png",
  },
  {
    title: "Company Creation & Configuration",
    icon: "🏢",
    img: "https://img.icons8.com/color/96/company.png",
  },
  {
    title: "Voucher Entry & Management",
    icon: "🧾",
    img: "https://img.icons8.com/color/96/invoice.png",
  },
  {
    title: "Inventory Management",
    icon: "📦",
    img: "https://img.icons8.com/color/96/warehouse.png",
  },
  {
    title: "GST & Taxation in Tally",
    icon: "💰",
    img: "https://img.icons8.com/color/96/tax.png",
  },
  {
    title: "Payroll Processing",
    icon: "👔",
    img: "https://img.icons8.com/color/96/payroll.png",
  },
  {
    title: "Reporting & Data Analysis",
    icon: "📊",
    img: "https://img.icons8.com/color/96/combo-chart.png",
  },
  {
    title: "Real-world Project Work",
    icon: "🛠️",
    img: "https://img.icons8.com/color/96/project.png",
  },
];

// Careers
const careers = [
  "Tally Accountant",
  "Finance Executive",
  "Inventory Manager",
  "Tax Consultant",
  "Payroll Specialist",
];

// FAQs
const faqs = [
  {
    question: "Is this Tally course suitable for beginners?",
    answer:
      "Absolutely! The course starts from basics and gradually covers advanced topics. No prior experience needed.",
  },
  {
    question: "Which version of Tally will I learn?",
    answer:
      "You will learn Tally Prime, the latest version, along with practical scenarios.",
  },
  {
    question: "Will I get hands-on practice?",
    answer:
      "Yes, every module includes practical exercises and real-world projects.",
  },
];

// Enroll Benefits

// Syllabus
const syllabus = [
  {
    module: "Module 1: Tally Basics",
    topics: ["Introduction to Tally", "Accounting Basics", "Company Creation"],
    icon: "📘",
    color: "#facc15",
  },
  {
    module: "Module 2: Transactions",
    topics: ["Voucher Entry", "Invoice Management", "Inventory Management"],
    icon: "🧾",
    color: "#22c55e",
  },
  {
    module: "Module 3: GST & Taxation",
    topics: ["GST Concepts", "Tax Reports", "Compliance"],
    icon: "💰",
    color: "#ef4444",
  },
  {
    module: "Module 4: Payroll",
    topics: ["Salary Structure", "Pay Slip Generation", "PF & ESI"],
    icon: "👔",
    color: "#3b82f6",
  },
  {
    module: "Module 5: Reporting & Analysis",
    topics: ["Financial Reports", "Data Analysis", "Project Work"],
    icon: "📊",
    color: "#8b5cf6",
  },
];

const gradients = [
  "linear-gradient(135deg, #fef3c7, #fde68a)", // yellow
  "linear-gradient(135deg, #dbeafe, #bfdbfe)", // blue
  "linear-gradient(135deg, #d1fae5, #a7f3d0)", // green
];

export default function Tally() {
  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
  }, []);

  return (
    <div
      style={{
        background: "linear-gradient(135deg, #f8fafc 0%, #e0e7ff 100%)",
        minHeight: "100vh",
      }}
    >
      {/* HEADER */}
      <header
        className="relative py-28 text-white text-center bg-cover bg-center rounded-b-2xl shadow-md"
        style={{
          backgroundImage:
            "url('https://i.pinimg.com/736x/90/ee/72/90ee7296f514754aa6eed38712729eac.jpg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        data-aos="fade-down"
      >
        {/* Overlay to improve text readability */}
        <div className="absolute inset-0 bg-black/50 rounded-b-2xl"></div>

        <div className="relative z-10">
          <h1
            className="text-4xl md:text-6xl font-extrabold mb-4"
            data-aos="fade-down"
          >
            Tally Course
          </h1>
          <p className="text-lg md:text-xl mt-2">
            Master accounting, GST, payroll & more with Tally Prime
          </p>

          {/* Enroll Button */}
          <Link
            to="/contact"
            className="mt-8 inline-block px-6 py-3 bg-yellow-400 text-indigo-900 font-semibold rounded-xl shadow-lg animate-bounce text-center"
          >
            Enroll Now 🚀
          </Link>
        </div>
      </header>

      <section
        style={{
          padding: "60px 20px",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <motion.div
          data-aos="zoom-in"
          whileHover={{ scale: 1.05 }}
          style={{
            background: gradients[0],
            borderRadius: "20px",
            padding: "40px",
            maxWidth: "900px", // increased width
            width: "100%", // responsive
            textAlign: "center",
            boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
          }}
        >
          <motion.div
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 2, repeat: Infinity, repeatType: "loop" }}
            style={{ fontSize: "4rem", color: "#1e3a8a", marginBottom: "20px" }}
          >
            <FaCalculator />
          </motion.div>
          <h2
            style={{
              fontWeight: "bold",
              fontSize: "2rem",
              marginBottom: "15px",
              color: "#1e3a8a",
            }}
          >
            What is Tally?
          </h2>
          <p style={{ color: "#555", fontSize: "1rem", lineHeight: "1.6" }}>
            Tally is a widely used accounting software that helps businesses
            manage accounting, inventory, payroll, and taxation. It simplifies
            financial operations, generates reports, and maintains accurate
            records. Tally supports GST compliance, invoicing, and banking
            transactions efficiently.
          </p>
        </motion.div>
      </section>

      <main style={{ maxWidth: 900, margin: "2rem auto", padding: "0 1rem" }}>
        {/* CURRICULUM */}
        <section>
          <h2
            style={{
              fontSize: "1.8rem",
              fontWeight: 600,
              marginBottom: "1.5rem",
              color: "#000",
              textAlign: "center",
            }}
            data-aos="fade-up"
          >
            Curriculum Highlights
          </h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: "1.5rem",
            }}
          >
            {curriculum.map((item, idx) => (
              <div
                key={item.title}
                data-aos="zoom-in"
                data-aos-delay={idx * 120}
                className="card-animate"
                style={{
                  background: "#fff",
                  borderRadius: "1rem",
                  boxShadow: "0 2px 12px #312e8133",
                  padding: "1.2rem",
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem",
                  color: "#000",
                }}
              >
                <img
                  src={item.img}
                  alt={item.title}
                  style={{
                    width: "3rem",
                    height: "3rem",
                  }}
                />
                <span style={{ fontWeight: 500 }}>{item.title}</span>
              </div>
            ))}
          </div>
        </section>

        {/* SYLLABUS */}
        <section style={{ marginTop: "3rem" }}>
          <h2
            style={{
              fontSize: "1.8rem",
              fontWeight: 600,
              marginBottom: "1.5rem",
              color: "#312e81",
              textAlign: "center",
            }}
            data-aos="fade-up"
          >
            Course Syllabus
          </h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: "1.5rem",
            }}
          >
            {syllabus.map((mod, idx) => (
              <div
                key={mod.module}
                data-aos="zoom-in"
                data-aos-delay={idx * 150}
                className="card-animate"
                style={{
                  background: mod.color,
                  borderRadius: "1rem",
                  padding: "1rem",
                  color: "#000",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    fontSize: "2rem",
                    marginBottom: "0.5rem",
                    animation: "bounce 2s infinite",
                  }}
                >
                  {mod.icon}
                </div>
                <h3 style={{ fontWeight: 600, marginBottom: "0.5rem" }}>
                  {mod.module}
                </h3>
                <ul style={{ listStyle: "none", padding: 0 }}>
                  {mod.topics.map((topic, i) => (
                    <li
                      key={i}
                      style={{
                        margin: "0.3rem 0",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                      }}
                    >
                      <span
                        style={{
                          animation: "pulse 2s infinite",
                          fontSize: "1.2rem",
                        }}
                      >
                        ▶️
                      </span>{" "}
                      {topic}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* CAREERS */}
        <section style={{ marginTop: "3rem" }}>
          <h2
            style={{
              fontSize: "1.8rem",
              fontWeight: 600,
              marginBottom: "1.5rem",
              color: "#312e81",
              textAlign: "center",
            }}
            data-aos="fade-up"
          >
            Career Opportunities
          </h2>
          <ul
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "1rem",
              justifyContent: "center",
              listStyle: "none",
              padding: 0,
            }}
          >
            {careers.map((role, idx) => (
              <li
                key={role}
                data-aos="flip-up"
                data-aos-delay={idx * 150}
                className="bounce-animate"
                style={{
                  background: "#6366f1",
                  color: "#fff",
                  borderRadius: "0.7rem",
                  padding: "0.7rem 1.2rem",
                  fontWeight: 500,
                  boxShadow: "0 2px 8px #6366f133",
                  fontSize: "1rem",
                }}
              >
                {role}
              </li>
            ))}
          </ul>
        </section>

        {/* FAQ */}
        <section style={{ marginTop: "3rem" }}>
          <h2
            style={{
              fontSize: "1.8rem",
              fontWeight: 600,
              marginBottom: "1.5rem",
              color: "#312e81",
              textAlign: "center",
            }}
            data-aos="fade-up"
          >
            Frequently Asked Questions
          </h2>
          <div style={{ display: "grid", gap: "1.2rem" }}>
            {faqs.map((faq, idx) => (
              <div
                key={faq.question}
                data-aos="fade-up"
                data-aos-delay={idx * 200}
                className="shake-animate"
                style={{
                  background: "#fff",
                  borderRadius: "0.8rem",
                  boxShadow: "0 2px 8px #312e8133",
                  padding: "1rem 1.2rem",
                }}
              >
                <strong style={{ color: "#6366f1" }}>{faq.question}</strong>
                <p style={{ margin: "0.5rem 0 0 0", color: "#312e81" }}>
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </section>
      </main>
      <footer
        style={{
          background: "#312e81",
          color: "#fff",
          padding: "2.5rem 0 2rem 0",
          textAlign: "center",
          borderRadius: "2rem 2rem 0 0",
          boxShadow: "0 4px 24px #312e8133",
        }}
        data-aos="fade-down"
      >
        {/* Enroll Button (Header) */}
        <Link
          to="/contact"
          className="mt-4 inline-block px-6 py-3 bg-yellow-400 text-indigo-900 font-semibold rounded-xl shadow-lg animate-bounce"
          style={{ boxShadow: "0 3px 12px #6366f166" }}
        >
          Enroll Now 🚀
        </Link>
      </footer>

      {/* Animations */}
      <style jsx>{`
        @keyframes jump {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        .card-animate {
          animation: dance 4s infinite ease-in-out;
        }
        @keyframes dance {
          0% {
            transform: rotate(0);
          }
          25% {
            transform: rotate(2deg);
          }
          50% {
            transform: rotate(-2deg);
          }
          75% {
            transform: rotate(2deg);
          }
          100% {
            transform: rotate(0);
          }
        }
        .bounce-animate {
          animation: bounce 2s infinite;
        }
        @keyframes bounce {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-8px);
          }
        }
        .shake-animate {
          animation: shake 3s infinite;
        }
        @keyframes shake {
          0%,
          100% {
            transform: translate(0, 0);
          }
          20% {
            transform: translate(-2px, 2px);
          }
          40% {
            transform: translate(2px, -2px);
          }
          60% {
            transform: translate(-2px, 2px);
          }
          80% {
            transform: translate(2px, -2px);
          }
        }
        @keyframes pulse {
          0%,
          100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.2);
          }
        }
      `}</style>
    </div>
  );
}
